import { Glow } from '@/components/ui';
import { cn } from '@/utils/cn';

import {
  HEADLINE_FROM,
  HEADLINE_TO,
  useHeadingZoomOut,
  useScrollZoom,
  useSectionReveal,
} from './useSectionReveal';

const STATS = [
  { to: 1000, compact: true, suffix: '+', label: 'Students' },
  { to: 20, suffix: '+', label: 'Startups' },
  { text: 'Coming Soon', label: 'Universities' },
  { to: 5, suffix: '/5', label: 'Satisfaction' },
  { to: 500, suffix: '+', label: 'Hires' },
];

function formatCount(value, compact) {
  const rounded = Math.round(value);
  if (compact && rounded >= 1000) return `${Math.round(rounded / 100) / 10}k`.replace('.0', '');
  return String(rounded);
}

export function TrustedBy() {
  const zoom = useScrollZoom();
  const heading = useHeadingZoomOut();

  // Roughly three seconds, on absolute positions rather than chained:
  //
  //   0.00  the orb swells in behind the copy
  //   0.10  the two headline lines rise out of their masks, out of focus
  //   0.85  each stat overshoots into place, one after the next
  //   0.95  its number starts climbing the moment the tile lands
  //   1.15  the labels arrive under the numbers they belong to
  //   2.30  the finished numbers flare once and settle
  //
  // The orb is scaled and never faded: `Glow` sets its own opacity inline, and
  // a reveal would finish by animating that to 1 -- a soft orb turning into a
  // flat disc.
  const root = useSectionReveal(({ timeline, select, reveal }) => {
    reveal('.js-trust-glow', { scale: 0.5 }, { duration: 1.8, ease: 'power2.out' }, 0);
    reveal('.js-trust-line', HEADLINE_FROM, HEADLINE_TO, 0.1);
    reveal(
      '.js-stat',
      { y: 40, scale: 0.85, opacity: 0, filter: 'blur(10px)' },
      { duration: 0.8, stagger: 0.12, ease: 'back.out(1.8)' },
      0.85,
    );
    reveal(
      '.js-stat-label',
      { y: 12, opacity: 0 },
      { duration: 0.6, stagger: 0.12, ease: 'power2.out' },
      1.15,
    );

    select('.js-count').forEach((element, index) => {
      const target = Number(element.dataset.to);
      const compact = element.dataset.compact === 'true';
      const counter = { value: 0 };

      timeline.to(
        counter,
        {
          value: target,
          duration: 1.1,
          ease: 'power2.out',
          onUpdate: () => {
            element.textContent = formatCount(counter.value, compact);
          },
        },
        0.95 + index * 0.1,
      );
    });

    // Not a `reveal`: text-shadow has no neutral to settle on, and the point is
    // a flare that comes and goes rather than a value that arrives. `yoyo`
    // spends the second half taking it back off. Bound to the whole value, so
    // "Coming Soon" flares with the numbers instead of sitting the beat out.
    timeline.fromTo(
      select('.js-stat-value'),
      { textShadow: '0 0 0 rgba(196, 176, 250, 0)' },
      {
        textShadow: '0 0 26px rgba(196, 176, 250, 0.55)',
        duration: 0.35,
        stagger: 0.06,
        ease: 'sine.inOut',
        repeat: 1,
        yoyo: true,
      },
      2.3,
    );
  });

  return (
    <section ref={root} className="wonder-screen relative overflow-x-clip py-20 lg:py-28">
      <div>
        <Glow
          className="js-trust-glow -top-20 left-1/2 -translate-x-1/2"
          size={760}
          opacity={0.14}
        />

        <div ref={zoom} className="wonder-container wonder-measure relative">
          <h2
            ref={heading}
            className="mx-auto max-w-288 text-center text-[clamp(2rem,3.15vw,3.75rem)] leading-[1.1] font-medium tracking-[-0.033em]"
          >
            {/*
            The break between the two lines is typeset, not wrapped: the design
            balances them deliberately. Each gets its own overflow-hidden
            wrapper so it can rise from behind an edge, and the space between
            them is load-bearing -- JSX drops the newline, so without it the
            accessible name runs "…earlycareers begin…".
          */}
            <span className="block overflow-hidden pb-[0.1em] mb-[-0.1em]">
              <span className="js-trust-line text-on-night block">
                Because we are rebuilding the way early
              </span>
            </span>{' '}
            <span className="block overflow-hidden pb-[0.1em] mb-[-0.1em]">
              <span className="js-trust-line text-on-night block">
                careers begin, <span className="text-iris-300">we are trusted by</span>
              </span>
            </span>
          </h2>

          <dl className="mt-16 grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:mt-24 lg:flex lg:justify-between">
            {/*
            Each tile is a column that fills its grid row, with the value taking
            whatever height is going and the label sitting on the floor. Grid
            items stretch by default, so every label lands on the same line even
            when one value needs two of them -- which "Coming Soon" does, now
            that the row is set to the shared measure rather than the full
            width of the screen. Stacked plainly, its label dropped a line below
            the other four and the row read as broken.
          */}
            {STATS.map((stat, index) => (
              <div
                key={stat.label}
                className={cn(
                  'js-stat flex flex-col text-center',
                  // The row is flush with the measure: the first value starts on
                  // the FAQ heading's left edge, the last ends on the list's right.
                  index === 0 && 'lg:text-left',
                  index === STATS.length - 1 && 'lg:text-right',
                )}
              >
                <dd
                  className={cn(
                    'js-stat-value text-on-night flex flex-1 items-center justify-center text-[clamp(1.75rem,2.9vw,3.5rem)] font-bold tracking-[-0.02em]',
                    index === 0 && 'lg:justify-start',
                    index === STATS.length - 1 && 'lg:justify-end',
                  )}
                >
                  {stat.text ?? (
                    <>
                      <span
                        className="js-count tabular-nums"
                        data-to={stat.to}
                        data-compact={String(Boolean(stat.compact))}
                      >
                        {formatCount(stat.to, stat.compact)}
                      </span>
                      {stat.suffix}
                    </>
                  )}
                </dd>
                <dt className="js-stat-label text-on-night-subtle mt-2 text-sm lg:text-lg">
                  {stat.label}
                </dt>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
